import { getAuth } from 'firebase/auth';

/**
 * Get Firebase auth token for API authentication
 */
async function getAuthToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
}

/**
 * Generate contract ID from linkId and timestamp
 * Format: linkId_timestamp
 * Example: "50XdZImcit6UNGcY0ppr_1751055650539"
 */
export function generateContractId(linkId, fileUrl) {
  // Extract timestamp from file URL
  // Example URL: clientes/IyioHoIvQak2Rm8oOmFq/50XdZImcit6UNGcY0ppr/contrato/1751055650539_Contrato.pdf
  const timestampMatch = fileUrl.match(/(\d{13})_/);
  if (!timestampMatch) {
    throw new Error('Could not extract timestamp from file URL');
  }
  const timestamp = timestampMatch[1];
  return `${linkId}_${timestamp}`;
}

/**
 * Get active conversation for a contract
 * GET /get-active-conversation?contractId={contractId}
 */
export async function getActiveConversation(contractId) {
  try {
    const token = await getAuthToken();
    const API_BASE = process.env.REACT_APP_API_URL;
    
    const response = await fetch(`${API_BASE}/get-active-conversation?contractId=${encodeURIComponent(contractId)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting active conversation:', error);
    throw error;
  }
}

/**
 * Create new conversation (deactivate current and start fresh)
 * POST /create-new-conversation
 */
export async function createNewConversation(contractId) {
  try {
    const token = await getAuthToken();
    const API_BASE = process.env.REACT_APP_API_URL;
    
    const response = await fetch(`${API_BASE}/create-new-conversation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contractId: contractId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating new conversation:', error);
    throw error;
  }
}

/**
 * Send message and get AI response with afianzadora recommendations
 * POST /chat-criterios
 */
export async function sendChatMessage(contractId, conversation) {
  try {
    const token = await getAuthToken();
    const API_BASE = process.env.REACT_APP_API_URL;
    
    const response = await fetch(`${API_BASE}/chat-criterios`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contractId: contractId,
        conversation: conversation
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
}

/**
 * Helper function to create a user message object
 */
export function createUserMessage(content) {
  return {
    type: 'user',
    content: content,
    timestamp: Date.now()
  };
}

/**
 * Helper function to format conversation for display
 */
export function formatTimestamp(timestamp) {
  return new Date(timestamp).toLocaleString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}